import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import tw from "tailwind-styled-components";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import NotFoundModel from "../../Components/NotFoundModel";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";
import SearchFilterBox from "../../Components/SearchFilterBox";
import { IoFilter } from "react-icons/io5";
import SummaryCards from "../../Components/Inventory/InventoryStock/SummaryCards";
import Table from "../../Components/Inventory/InventoryStock/Table";
import FilterModal from "../../Components/Inventory/InventoryStock/FilterModal";
import SideModal from "../../Components/SideModal";

const InventoryStock = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isFocused, setIsFocused] = useState(false);
    const user = useSelector((state) => state.UserReducer.user);
    const [apiData, setApiData] = useState([]);
    const [masterData, setMasterData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [stockFilter, setStockFilter] = useState(""); // LS - low stock, OOS - out of stock
    const [filterValue, setFilterValue] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState({
        value: "",
        label: "",
        title: "",
    });

    // Summary state
    const [summary, setSummary] = useState({
        totalItems: 0,
        lowStock: 0,
        outOfStock: 0,
        currentStockValue: 0
    });

    const searchOptions = [
        { label: "Item", value: 1 },
    ];

    const panelRef = useRef(null);
    const btnRef = useRef(null);

    // Parse filter value
    const parseFilterValue = (filterValue) => {
        if (!filterValue) return null;
        try {
            return JSON.parse(filterValue);
        } catch (error) {
            return filterValue;
        }
    };

    // Format filter for display
    const formatFilterForDisplay = useCallback((parsedFilter) => {
        if (!parsedFilter?.categories) return null;

        const categoryCount = parsedFilter.categories?.length || 0;

        if (categoryCount === 0) return null;

        return {
            value: JSON.stringify(parsedFilter),
            label: `Filter (${categoryCount})`,
            title: `Category Filter: ${categoryCount} categories selected`
        };
    }, []);

    // Fetch inventory stock
    const fetchFunction = async () => {
        const params = new URLSearchParams();

        if (categoryFilter) params.append("categoryFilters", categoryFilter);
        if (stockFilter) params.append("stockFilter", stockFilter);
        if (searchQuery) {
            params.append("s", searchQuery);
            if (filterType) {
                params.append("t", filterType);
            }
        }
        const response = await axios.get(
            `${Config.apiUrl}/kitchen/inventory/stock/list?${params.toString()}`,
            {
                headers: {
                    authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            toast.error(res.msg || "Failed to fetch inventory");
        } else {
            const stocks = res.inventoryStocks || [];
            setApiData(stocks);
            setMasterData(stocks);
            setIsFocused(true);

            // Calculate summary
            const lowStockItems = stocks.filter(item => item.currentCount > 0 && item.currentCount < 5);
            const outOfStockItems = stocks.filter(item => item.currentCount === 0);
            setSummary({
                totalItems: stocks.length,
                lowStock: lowStockItems.length,
                outOfStock: outOfStockItems.length,
                currentStockValue: Number(res?.currentStockValue || 0)
            });
        }
    };

    const getError = () => {
        navigate("/no-access");
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            getListMutate();
        }, 200);
        return () => clearTimeout(handler);
    }, [categoryFilter, stockFilter, searchQuery, filterType]);

    // Handle search
    const handleFilterChange = (values) => {
        setSearchQuery(values.searchQuery || "");
        setFilterType(values.filterType || "");
    };

    // Handle summary box click
    const onSummaryBoxClick = (filterType) => {
        let filteredData;
        if (filterType === "LS") {
            // Low Stock
            filteredData = masterData.filter(item => item.currentCount > 0 && item.currentCount < 5);
        } else if (filterType === "OOS") {
            // Out of Stock
            filteredData = masterData.filter(item => item.currentCount === 0);
        } else {
            // All
            filteredData = masterData;
        }
        setApiData(filteredData);
    };

    // Handle apply filter from FilterModal
    const onApplyFilter = (selectedFilters) => {
        // Extract category IDs from selected filters
        const categoryIds = selectedFilters.categories.map(cat => cat.value).join(',');

        // Set category filter for API
        setCategoryFilter(categoryIds);

        // Update filter value for display
        setFilterValue(JSON.stringify(selectedFilters));

        // Update active filter display
        const formattedFilter = formatFilterForDisplay(selectedFilters);
        if (formattedFilter) {
            setActiveFilter(formattedFilter);
        } else {
            setActiveFilter({ value: "", label: "", title: "" });
        }

        // Close modal
        setIsFilterOpen(false);
    };

    // Handle filter close
    const handleFilterClose = () => {
        setIsFilterOpen(false);
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchQuery("");
        setCategoryFilter("");
        setStockFilter("");
        setFilterType("");
        setFilterValue("");
        setActiveFilter({ value: "", label: "", title: "" });
        setIsFilterOpen(false);
    };

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Stock
                    <Underline />
                </BoxTitle>
            </BoxHead>

            {(isLoading || isRefetching) && !isFocused && (
                <SummarySkeletonLoading count={3} />
            )}

            <SideModal
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
            >
                <FilterModal
                    onApplyFilter={onApplyFilter}
                    handleClose={handleFilterClose}
                    currentFilter={parseFilterValue(filterValue)}
                    handleClearFilters={handleClearFilters}
                />
            </SideModal>

            {isFocused && summary && (
                <>
                    <SummaryCards
                        summaryData={summary}
                        onClick={onSummaryBoxClick}
                    />

                    <FilterTableWrapper>
                        <FilterWrapperWithSearch>
                            <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                                <SearchFilterBox
                                    options={searchOptions}
                                    handleFilterChange={handleFilterChange}
                                    handleClearFilters={handleClearFilters}
                                />
                            </div>
                            <div className="flex gap-2 justify-end items-center">
                                <div className="relative">
                                    <FilterBtn
                                        ref={btnRef}
                                        type="button"
                                        title={activeFilter.title || "Open Filter"}
                                        onClick={() => setIsFilterOpen((v) => !v)}
                                    >
                                        <IoFilter size={14} color="#9CA3AF" />
                                        {activeFilter?.value && (
                                            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-yellow-500 border border-white" />
                                        )}
                                    </FilterBtn>
                                </div>
                            </div>
                        </FilterWrapperWithSearch>

                        {!error && !isLoading && data && (
                            <TableWrapper>
                                <Table
                                    apiData={error ? [] : apiData}
                                />
                                {(error || apiData.length === 0) && !isLoading && (
                                    <NotFoundModel msg={"No inventory items found."} />
                                )}
                            </TableWrapper>
                        )}
                    </FilterTableWrapper>
                </>
            )}

            {(isLoading || isRefetching) && <Loading />}
        </BoxContainer>
    );
};

const TableWrapper = tw.h1`mt-4 bg-white rounded-md`;
const FilterBtn = tw.button`flex items-center border whitespace-nowrap border-gray-300 justify-center rounded-md px-4 cursor-pointer bg-white hover:bg-gray-200 h-10 text-sm ml-auto`;

export default InventoryStock;